import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { tenantKeys } from "@/lib/queryKeys";
import { getTenantCurrent } from "@/services/authService";

export type InvoiceCreationBlockedReason =
  | "literal_e"
  | "dgi_test"
  | "provider_pending";

/**
 * True when the tenant cannot create invoices from the portal (Literal E pending
 * verification; DGI test / ePrueba mode until homologation; or Fiscalo not yet
 * registered as the DGI software provider — new tenants only, existing emitters
 * are grandfathered via hasEmittedCfe).
 */
export function useInvoiceCreationBlocked(): {
  blocked: boolean;
  reason: InvoiceCreationBlockedReason | null;
  isLoading: boolean;
} {
  const { user } = useAuth();
  const { data: tenant, isLoading } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });

  const literalEPending =
    Boolean(tenant?.literalE) &&
    tenant?.literalEVerified !== true;

  // bypassDgi tenants (demo / showcase accounts) never hit DGI — the
  // ePrueba gate exists to stop accidental sandbox issuance, which is
  // moot when no DGI call ever happens. Unblock so the demo can issue
  // invoices end-to-end.
  const dgiTestBlocked =
    tenant?.dgiEnvironment === "test" && !tenant?.bypassDgi;

  // Fiscalo not yet registered as the DGI software provider. Only blocks a NEW
  // tenant whose prerequisites are met but who hasn't activated. Grandfather
  // existing emitters (hasEmittedCfe) + demo tenants (bypassDgi), and require the
  // affirmative prerequisitesMet so an old backend that doesn't send these fields
  // (defaults false) never blocks — safe to ship before that backend deploys.
  const providerPending =
    Boolean(tenant?.fiscaloConfig?.prerequisitesMet) &&
    !tenant?.emisionEnabled &&
    !tenant?.hasEmittedCfe &&
    !tenant?.bypassDgi;

  if (literalEPending) {
    return { blocked: true, reason: "literal_e", isLoading };
  }
  if (dgiTestBlocked) {
    return { blocked: true, reason: "dgi_test", isLoading };
  }
  if (providerPending) {
    return { blocked: true, reason: "provider_pending", isLoading };
  }
  return { blocked: false, reason: null, isLoading };
}

/** i18n key for disabled primary invoice actions (tooltip). */
export function invoiceCreationBlockedTooltipKey(
  reason: InvoiceCreationBlockedReason | null
):
  | "dashboard.header.createInvoiceDisabledLiteralE"
  | "dashboard.header.createInvoiceDisabledTestMode"
  | "dashboard.header.createInvoiceDisabledProviderPending"
  | null {
  if (reason === "literal_e") return "dashboard.header.createInvoiceDisabledLiteralE";
  if (reason === "dgi_test") return "dashboard.header.createInvoiceDisabledTestMode";
  if (reason === "provider_pending")
    return "dashboard.header.createInvoiceDisabledProviderPending";
  return null;
}

/** i18n key for dashboard empty-state copy when creation is blocked. */
export function invoiceCreationBlockedEmptyExplanationKey(
  reason: InvoiceCreationBlockedReason | null
):
  | "dashboard.empty.invoicingBlockedLiteralE"
  | "dashboard.empty.invoicingBlockedTestMode"
  | "dashboard.empty.invoicingBlockedProviderPending"
  | null {
  if (reason === "literal_e") return "dashboard.empty.invoicingBlockedLiteralE";
  if (reason === "dgi_test") return "dashboard.empty.invoicingBlockedTestMode";
  if (reason === "provider_pending")
    return "dashboard.empty.invoicingBlockedProviderPending";
  return null;
}
